<script setup lang="ts">
import type { FieldSize } from '@/components/fields/KTextField.vue';

import { computed, ref } from 'vue';

import KBadge from '@/components/badges/KBadge.vue';
import KCheckboxField from '@/components/fields/KCheckboxField.vue';
import NoDataSelectVListItem from '@/components/items/NoDataSelectVListItem.vue';
import useAutocompleteField from '@/composables/useAutocompleteField';
import useField from '@/composables/useField';

import fieldStyles from '@/styles/field.module.scss';
import listItemStyles from '@/styles/listItem.module.scss';

export interface ItemProps {
  subtitle?: string;
  titleIcon?: string;
}

export interface Item {
  value: string | number;
  title: string;
  props?: ItemProps;
}

export interface Props {
  modelValue?: Item[] | string[] | number[] | null;
  size?: FieldSize;
  items?: readonly Item[] | readonly string[] | readonly number[];
  plusCount?: number;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  size: 'md',
  items: () => [],
  plusCount: 2,
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const fieldValue = ref(props.modelValue);
const field = ref<HTMLFormElement | null>(null);

useField(props, fieldValue, (newFieldValue) => {
  emit('update:modelValue', newFieldValue as (typeof props)['modelValue']);
});
const { handleEnterKeydown } = useAutocompleteField(fieldValue, null, field);

const sizeClass = computed(() => 'size-' + props.size);

const allSelected = computed(() => {
  if (!fieldValue.value) return false;
  return fieldValue.value.length === props.items.length;
});
const someSelected = computed(() => {
  if (!fieldValue.value) return false;
  return fieldValue.value.length > 0;
});

function toggleAll(): void {
  if (allSelected.value) {
    fieldValue.value = [];
  } else {
    // @ts-expect-error
    fieldValue.value = props.items.map((item) => item.value);
  }
}
// TODO use this, therwise with [{ value, title }] items filed shows value number in the input, but should show title
// return-object
// item-title
// item-value
</script>

<template>
  <v-select
    ref="field"
    v-model="fieldValue"
    :items="props.items"
    :class="[
      'g-v-text-field',
      $style.kMultipleSelectField,
      fieldStyles[sizeClass],
    ]"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    menu-icon="mdi-chevron-down"
    multiple
    closable-chips
    data-testid="kMultipleSelectField"
    transition="slide-y-transition"
    @keydown.enter="handleEnterKeydown"
  >
    <template v-slot:selection="{ item, index }">
      <v-chip
        v-if="index < plusCount"
        :class="$style.chip"
        variant="flat"
        :color="$style.chipColor"
        :text="item.title"
        :prepend-icon="item.props.titleIcon"
        size="x-small"
        label
      >
      </v-chip>
      <KBadge
        v-if="index === plusCount && fieldValue?.length"
        :class="$style.badge"
        class="mx-n1"
        :content="'+' + (fieldValue.length - plusCount)"
        inline
      />
    </template>
    <template #no-data>
      <NoDataSelectVListItem />
    </template>
    <template v-if="props.items.length" #prepend-item>
      <v-list-item
        :class="listItemStyles.listItem"
        :active-class="listItemStyles.listItemActive"
        title="All"
        :active="allSelected"
        @click="toggleAll"
      >
        <template #prepend>
          <KCheckboxField
            class="my-n2"
            tabindex="-1"
            :indeterminate="someSelected && !allSelected"
            :model-value="someSelected"
            hide-details
          />
        </template>
      </v-list-item>
      <v-divider class="mt-2"></v-divider>
    </template>
    <template #item="itemData">
      <v-list-item
        :class="listItemStyles.listItem"
        :active-class="listItemStyles.listItemActive"
        v-bind="itemData.props"
      >
        <template #prepend="{ isSelected }">
          <v-list-item-action>
            <KCheckboxField
              class="my-n2"
              tabindex="-1"
              :model-value="isSelected"
              hide-details
            />
          </v-list-item-action>
        </template>
        <template #title="{ title }">
          <v-icon
            v-if="itemData.item.props.titleIcon"
            :class="listItemStyles.titleIcon"
            :icon="itemData.item.props.titleIcon"
          >
          </v-icon>
          {{ title }}
        </template>
      </v-list-item>
    </template>
  </v-select>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
  chipColor: colors.$color-brand-200;
}

.kMultipleSelectField {
  :global {
    .v-field__input input {
      margin: 0;
      padding: 0;
    }
    .v-select__selection {
      margin-top: 0;
      margin-inline-end: 6px !important;
    }
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}

.chip {
  color: colors.$color-text-primary !important;

  :global {
    .v-chip__prepend {
      font-size: 10px;
    }
    .v-chip__content {
      font-size: 16px;
    }
    .v-chip__close {
      background: transparent;
      border: 0;
      margin: 0;
      padding: 0;
      font-size: 16px;
    }
  }
}
.badge {
  position: absolute;
  margin-top: -1px;
}
</style>
